import React, { useEffect, useState, useCallback } from "react";
import { adminClient } from "../../../lib/admin";
import {
  Scale, ShieldCheck, AlertTriangle, FileText, Save, ArrowLeft, Download,
  History, CheckCircle2, Lock, Globe,
} from "lucide-react";

const STATUS_CLS = {
  draft: "bg-black/10 text-black/60", under_review: "bg-amber-100 text-amber-700",
  owner_approved: "bg-blue-100 text-blue-700", attorney_reviewed: "bg-purple-100 text-purple-700",
  published: "bg-green-100 text-green-700", retired: "bg-black/10 text-black/40",
};
const NEXT = {
  draft: [["under_review", "Submit for Review"]],
  under_review: [["owner_approved", "Owner Approve"], ["draft", "Back to Draft"]],
  owner_approved: [["attorney_reviewed", "Mark Attorney Reviewed"], ["published", "Publish"]],
  attorney_reviewed: [["published", "Publish"]],
  published: [["retired", "Retire"]],
  retired: [["draft", "Reopen Draft"]],
};
const LANGS = ["en", "fr", "ht", "es"];
const FACT_LABELS = {
  state_of_formation: "State of formation", legal_address: "Registered legal address",
  governing_law: "Governing law / jurisdiction", arbitration_venue: "Arbitration venue",
  liability_cap: "Limitation of liability cap", merchant_of_record: "Merchant of record",
  importer_of_record: "Importer of record", tax_responsibility: "Tax collection & remittance responsibility",
  legal_contact: "Legal contact", privacy_contact: "Privacy contact", dmca_contact: "DMCA contact",
};

export default function AdminLegal() {
  const c = adminClient();
  const [tab, setTab] = useState("facts");
  const [config, setConfig] = useState(null);
  const [docs, setDocs] = useState([]);
  const [detail, setDetail] = useState(null);
  const [lang, setLang] = useState("en");
  const [bodyDraft, setBodyDraft] = useState("");
  const [busy, setBusy] = useState(false);

  const loadConfig = useCallback(async () => setConfig((await c.get("/admin/legal/config")).data), []); // eslint-disable-line
  const loadDocs = useCallback(async () => setDocs((await c.get("/admin/legal/documents")).data.documents), []); // eslint-disable-line
  useEffect(() => { loadConfig(); loadDocs(); }, [loadConfig, loadDocs]);

  const openDoc = async (key) => {
    const d = (await c.get(`/admin/legal/documents/${key}`)).data.document;
    setDetail(d); setLang("en"); setBodyDraft(d.draft?.body || "");
  };
  const refreshDoc = async () => { if (detail) await openDoc(detail.key); await loadDocs(); await loadConfig(); };

  const saveFacts = async (updates) => {
    setBusy(true);
    try { setConfig((await c.put("/admin/legal/config", { updates })).data); await loadDocs(); }
    finally { setBusy(false); }
  };

  const saveDraft = async () => {
    setBusy(true);
    try {
      const payload = lang === "en" ? { body: bodyDraft } : { translations: { [lang]: bodyDraft } };
      await c.put(`/admin/legal/documents/${detail.key}/draft`, payload); await refreshDoc();
    } finally { setBusy(false); }
  };

  const doTransition = async (to) => {
    setBusy(true);
    try {
      const body = { to };
      if (to === "attorney_reviewed") body.attorney_status = "reviewed";
      await c.post(`/admin/legal/documents/${detail.key}/transition`, body); await refreshDoc();
    } catch (e) {
      const d = e?.response?.data?.detail;
      alert(d?.reason === "facts_unresolved" ? `Resolve required legal facts first: ${(d.unresolved || []).join(", ")}` : (d?.reason || "Action failed"));
    } finally { setBusy(false); }
  };

  const rollback = async (version) => { if (!window.confirm(`Roll back published version to v${version}?`)) return; await c.post(`/admin/legal/documents/${detail.key}/rollback`, { version }); await refreshDoc(); };

  const pdf = async (source) => {
    const r = await c.get(`/admin/legal/documents/${detail.key}/pdf`, { params: { source, lang }, responseType: "blob" });
    window.open(window.URL.createObjectURL(r.data), "_blank");
  };

  // ---------- Document editor ----------
  if (detail) {
    const d = detail;
    return (
      <div data-testid="legal-doc-editor">
        <button onClick={() => setDetail(null)} className="flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-black/50 hover:text-taven-orange mb-4"><ArrowLeft className="w-4 h-4" /> All Documents</button>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div><h2 className="text-xl font-black font-display">{d.title}</h2><p className="text-xs text-black/40">{d.canonical ? "Canonical" : "Preserved"} • key {d.key}</p></div>
          <span className={`text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${STATUS_CLS[d.status]}`} data-testid="legal-doc-status">{d.status.replace(/_/g, " ")}</span>
        </div>

        {!config?.publish_ready && <div className="border border-amber-300 bg-amber-50 p-3 mb-4 text-sm text-amber-800 flex items-center gap-2" data-testid="legal-publish-blocked"><AlertTriangle className="w-4 h-4" /> Publication is blocked until all required legal facts are resolved ({config?.unresolved?.length} remaining).</div>}
        <div className="border border-black/10 bg-white p-3 mb-4 text-xs text-black/60 flex items-center gap-2"><Lock className="w-3.5 h-3.5" /> Owner approval is not attorney approval. Nothing is published as final until reviewed. All bodies show a DRAFT banner until published.</div>

        <div className="flex gap-1 mb-2">
          {LANGS.map((l) => <button key={l} onClick={() => { setLang(l); setBodyDraft(l === "en" ? (d.draft?.body || "") : (d.draft?.translations?.[l] || "")); }} className={`px-3 py-1.5 text-xs font-bold uppercase ${lang === l ? "bg-taven-black text-white" : "border border-black/15"}`} data-testid={`legal-lang-${l}`}>{l}</button>)}
        </div>
        <textarea value={bodyDraft} onChange={(e) => setBodyDraft(e.target.value)} rows={14} className="w-full border border-black/15 p-3 text-sm font-mono" data-testid="legal-body" />
        <div className="flex flex-wrap items-center gap-2 mt-2">
          <button onClick={saveDraft} disabled={busy} className="inline-flex items-center gap-1 bg-taven-black text-white px-4 py-2.5 text-xs font-bold uppercase tracking-wide hover:bg-taven-orange disabled:opacity-50" data-testid="legal-save-draft"><Save className="w-3.5 h-3.5" /> Save Draft ({lang.toUpperCase()})</button>
          {(NEXT[d.status] || []).map(([to, label]) => (
            <button key={to} onClick={() => doTransition(to)} disabled={busy} className={`px-4 py-2.5 text-xs font-bold uppercase tracking-wide border ${to === "published" ? "bg-green-600 text-white border-green-600" : "border-black/15 hover:bg-taven-gray"} disabled:opacity-50`} data-testid={`legal-to-${to}`}>{label}</button>
          ))}
          <button onClick={() => pdf("draft")} className="inline-flex items-center gap-1 border border-black/15 px-3 py-2.5 text-xs font-bold uppercase hover:bg-taven-gray" data-testid="legal-pdf-draft"><Download className="w-3.5 h-3.5" /> Draft PDF</button>
          {d.published && <button onClick={() => pdf("published")} className="inline-flex items-center gap-1 border border-black/15 px-3 py-2.5 text-xs font-bold uppercase hover:bg-taven-gray" data-testid="legal-pdf-pub"><Download className="w-3.5 h-3.5" /> Published PDF</button>}
        </div>

        <div className="grid md:grid-cols-2 gap-5 mt-6">
          <div className="border border-black/10 bg-white p-4">
            <h3 className="font-black font-display uppercase text-xs tracking-wide mb-2">Resolved Preview (facts substituted)</h3>
            <pre className="text-[11px] whitespace-pre-wrap max-h-56 overflow-y-auto text-black/70" data-testid="legal-resolved">{d.resolved_preview}</pre>
          </div>
          <div className="border border-black/10 bg-white p-4">
            <h3 className="font-black font-display uppercase text-xs tracking-wide mb-2 flex items-center gap-1"><History className="w-4 h-4" /> Version History</h3>
            {(d.versions || []).length === 0 ? <p className="text-sm text-black/40">No published versions.</p> :
              (d.versions || []).slice().reverse().map((v) => (
                <div key={v.version} className="flex items-center justify-between py-1.5 border-b border-black/5 text-sm" data-testid={`legal-version-${v.version}`}>
                  <span>v{v.version} {v.material && <span className="text-[10px] bg-red-100 text-red-700 px-1 rounded">material</span>} <span className="text-black/30 text-xs">{new Date(v.published_at).toLocaleDateString()}</span></span>
                  <button onClick={() => rollback(v.version)} className="text-xs font-bold uppercase text-taven-orange hover:underline" data-testid={`legal-rollback-${v.version}`}>Roll back</button>
                </div>
              ))}
          </div>
        </div>
        <div className="border border-black/10 bg-white p-4 mt-5">
          <h3 className="font-black font-display uppercase text-xs tracking-wide mb-2">Audit</h3>
          <ol className="space-y-1 max-h-48 overflow-y-auto" data-testid="legal-audit">{(d.audit || []).map((a, i) => <li key={i} className="text-xs"><span className="font-bold">{a.action}</span> — {a.actor} <span className="text-black/30">{new Date(a.at).toLocaleString()}</span></li>)}</ol>
        </div>
      </div>
    );
  }

  // ---------- Overview (facts + documents) ----------
  return (
    <div data-testid="admin-legal">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-black font-display flex items-center gap-2"><Scale className="w-5 h-5 text-taven-gold" /> Legal Configuration Center</h2>
        <div className="flex gap-1">
          <button onClick={() => setTab("facts")} className={`px-4 py-2 text-xs font-bold uppercase ${tab === "facts" ? "bg-taven-black text-white" : "border border-black/15"}`} data-testid="legal-tab-facts">Legal Facts</button>
          <button onClick={() => setTab("docs")} className={`px-4 py-2 text-xs font-bold uppercase ${tab === "docs" ? "bg-taven-black text-white" : "border border-black/15"}`} data-testid="legal-tab-docs">Documents</button>
        </div>
      </div>

      {tab === "facts" && config && <FactsForm config={config} onSave={saveFacts} busy={busy} />}

      {tab === "docs" && (
        <>
          {!config?.publish_ready && <div className="border border-amber-300 bg-amber-50 p-3 mb-4 text-sm text-amber-800 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> {config?.unresolved?.length} required legal fact(s) unresolved — publication is blocked until completed.</div>}
          <div className="overflow-x-auto border border-black/10">
            <table className="w-full text-sm" data-testid="legal-doc-table">
              <thead className="bg-taven-gray/50 text-left text-xs uppercase tracking-wide"><tr><th className="p-3">Document</th><th className="p-3">Type</th><th className="p-3">Status</th><th className="p-3">Published</th><th className="p-3">Accept. ver</th><th className="p-3"></th></tr></thead>
              <tbody>
                {docs.map((d) => (
                  <tr key={d.key} className="border-t border-black/5 hover:bg-taven-gray/30">
                    <td className="p-3 font-bold">{d.title}</td>
                    <td className="p-3 text-xs">{d.canonical ? "Canonical" : "Preserved"}</td>
                    <td className="p-3"><span className={`text-[11px] font-bold uppercase px-2 py-0.5 rounded-full ${STATUS_CLS[d.status]}`}>{d.status.replace(/_/g, " ")}</span></td>
                    <td className="p-3 text-xs">{d.published_version ? `v${d.published_version}` : "—"}</td>
                    <td className="p-3 text-xs">{d.acceptance_version}</td>
                    <td className="p-3"><button onClick={() => openDoc(d.key)} className="text-xs font-bold uppercase text-taven-orange hover:underline" data-testid={`legal-open-${d.key}`}>Manage</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

const FactsForm = ({ config, onSave, busy }) => {
  const [vals, setVals] = useState(() => {
    const o = {}; Object.keys(FACT_LABELS).forEach((k) => { o[k] = config.config[k] === config.owner_token ? "" : (config.config[k] || ""); }); return o;
  });
  return (
    <div data-testid="legal-facts-form">
      <div className="border border-black/10 bg-white p-4 mb-4">
        <h3 className="font-black font-display uppercase text-xs tracking-wide mb-3 flex items-center gap-1"><Lock className="w-4 h-4" /> Confirmed (locked)</h3>
        <div className="grid sm:grid-cols-3 gap-3 text-sm">
          <div><span className="text-black/40 text-xs uppercase">Entity</span><p className="font-bold">{config.config.entity_name}</p></div>
          <div><span className="text-black/40 text-xs uppercase">Brand</span><p className="font-bold">{config.config.brand}</p></div>
          <div><span className="text-black/40 text-xs uppercase">Slogan</span><p className="font-bold">{config.config.slogan}</p></div>
        </div>
      </div>
      {config.publish_ready
        ? <div className="border border-green-300 bg-green-50 p-3 mb-4 text-sm text-green-700 flex items-center gap-2" data-testid="legal-facts-ready"><ShieldCheck className="w-4 h-4" /> All required legal facts resolved — documents can be published.</div>
        : <div className="border border-amber-300 bg-amber-50 p-3 mb-4 text-sm text-amber-800 flex items-center gap-2" data-testid="legal-facts-unresolved"><AlertTriangle className="w-4 h-4" /> {config.unresolved.length} fact(s) still marked “[[OWNER OR ATTORNEY INPUT REQUIRED]]”. Leave blank if unknown — publication stays blocked.</div>}
      <div className="border border-black/10 bg-white p-4">
        <h3 className="font-black font-display uppercase text-xs tracking-wide mb-3">Owner / Attorney Facts</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          {Object.entries(FACT_LABELS).map(([k, label]) => (
            <label key={k} className="block"><span className="text-xs font-bold uppercase tracking-wide text-black/60">{label}{config.unresolved.includes(k) && <span className="text-amber-600"> • required</span>}</span>
              <input value={vals[k]} onChange={(e) => setVals((v) => ({ ...v, [k]: e.target.value }))} placeholder="[[OWNER OR ATTORNEY INPUT REQUIRED]]" className="mt-1 w-full border border-black/15 px-3 py-2.5 text-sm" data-testid={`fact-${k}`} />
            </label>
          ))}
        </div>
        <button onClick={() => onSave(vals)} disabled={busy} className="mt-4 inline-flex items-center gap-1 bg-taven-black text-white px-5 py-2.5 text-xs font-bold uppercase tracking-wide hover:bg-taven-orange disabled:opacity-50" data-testid="legal-save-facts"><Save className="w-3.5 h-3.5" /> Save Legal Facts</button>
      </div>
    </div>
  );
};
